// RatesBoard.js — Spot, forward curve, CB reference and benchmark rate panel
// Mounted above the RFQ form in the Exchange Rates (FX) tab

import React, { useState, useEffect, useCallback } from 'react';
import { ArrowRightLeft, TrendingUp, Building2, Activity } from 'lucide-react';
import { SUPPORTED_PAIRS } from '../../lib/lp/constants';
import { MARKET_CONTEXT_KENYA } from '../../data/dealingMockData';
import { BENCHMARK_RATES } from '../../data/yieldConstants';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.forwardsflow.com/api/v1';

// Fallback data for other currency pairs (modelled on MARKET_CONTEXT_KENYA)
const FALLBACK_CONTEXTS = {
  KES: { spot: 131.20, cbRate: 10.0, cbName: 'CBK', forwardCurve: MARKET_CONTEXT_KENYA.forwardCurve },
  UGX: { spot: 3725.0, cbRate: 10.25, cbName: 'BOU', forwardCurve: [
    { tenor: '1M', rate: 3738, points: 13 }, { tenor: '3M', rate: 3762, points: 37 },
    { tenor: '6M', rate: 3810, points: 85 }, { tenor: '12M', rate: 3895, points: 170 },
  ]},
  TZS: { spot: 2535.0, cbRate: 6.0, cbName: 'BOT', forwardCurve: [
    { tenor: '1M', rate: 2541, points: 6 }, { tenor: '3M', rate: 2555, points: 20 },
    { tenor: '6M', rate: 2580, points: 45 }, { tenor: '12M', rate: 2625, points: 90 },
  ]},
  NGN: { spot: 1580.0, cbRate: 26.25, cbName: 'CBN', forwardCurve: [
    { tenor: '1M', rate: 1598, points: 18 }, { tenor: '3M', rate: 1635, points: 55 },
    { tenor: '6M', rate: 1710, points: 130 }, { tenor: '12M', rate: 1850, points: 270 },
  ]},
  RWF: { spot: 1320.0, cbRate: 7.0, cbName: 'BNR', forwardCurve: [
    { tenor: '1M', rate: 1323, points: 3 }, { tenor: '3M', rate: 1330, points: 10 },
    { tenor: '6M', rate: 1342, points: 22 }, { tenor: '12M', rate: 1365, points: 45 },
  ]},
};

const getLocalCurrency = (pair) => pair.split('/')[1];
const getSourceCurrency = (pair) => pair.split('/')[0];

const RatesBoard = () => {
  const [pair, setPair] = useState('USD/KES');
  const [ratesData, setRatesData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchRates = useCallback(async (selectedPair) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/estimator/data?pair=${encodeURIComponent(selectedPair)}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (json.success && json.data) {
        setRatesData(json.data);
      } else {
        setRatesData(null);
      }
    } catch {
      setRatesData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRates(pair);
  }, [pair, fetchRates]);

  // Resolve display data: API first, then fallback
  const localCcy = getLocalCurrency(pair);
  const sourceCcy = getSourceCurrency(pair);
  const fallback = FALLBACK_CONTEXTS[localCcy] || FALLBACK_CONTEXTS.KES;

  const spot = ratesData?.spot ?? fallback.spot;
  const cbRate = ratesData?.cbRate ?? fallback.cbRate;
  const cbName = ratesData?.cbName ?? fallback.cbName;
  const forwardCurve = ratesData?.forwardCurve ?? fallback.forwardCurve;
  const benchmarkRate = BENCHMARK_RATES[sourceCcy] ?? BENCHMARK_RATES.USD;

  return (
    <div className="bg-white border border-zinc-200 rounded-none p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500">Rates Board</h3>
        <div className="flex items-center gap-2">
          <label className="font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400">Pair</label>
          <select
            value={pair}
            onChange={(e) => setPair(e.target.value)}
            className="font-mono text-xs border border-zinc-200 rounded-none px-2 py-1 focus:outline-none focus:border-zinc-400"
          >
            {SUPPORTED_PAIRS.map(p => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          {loading && <span className="text-[10px] font-mono text-zinc-400 animate-pulse">loading...</span>}
        </div>
      </div>

      {/* Key rates row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="border border-zinc-100 p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <ArrowRightLeft className="w-3.5 h-3.5 text-zinc-400" />
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400">Spot</span>
          </div>
          <p className="font-mono text-lg font-bold text-black">{spot.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          <p className="font-mono text-[10px] text-zinc-400">{pair}</p>
        </div>
        <div className="border border-zinc-100 p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <Building2 className="w-3.5 h-3.5 text-zinc-400" />
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400">CB Reference</span>
          </div>
          <p className="font-mono text-lg font-bold text-black">{cbRate.toFixed(2)}%</p>
          <p className="font-mono text-[10px] text-zinc-400">{cbName} policy rate</p>
        </div>
        <div className="border border-zinc-100 p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <Activity className="w-3.5 h-3.5 text-zinc-400" />
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400">Benchmark</span>
          </div>
          <p className="font-mono text-lg font-bold text-black">{benchmarkRate.toFixed(2)}%</p>
          <p className="font-mono text-[10px] text-zinc-400">{sourceCcy} risk-free rate</p>
        </div>
        <div className="border border-zinc-100 p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <TrendingUp className="w-3.5 h-3.5 text-zinc-400" />
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400">Rate Differential</span>
          </div>
          <p className="font-mono text-lg font-bold text-black">{(cbRate - benchmarkRate).toFixed(2)}%</p>
          <p className="font-mono text-[10px] text-zinc-400">CB vs benchmark</p>
        </div>
      </div>

      {/* Forward curve */}
      <div>
        <h4 className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-3">Forward Curve</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200">
                <th className="text-left py-2 px-3 font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400">Tenor</th>
                <th className="text-right py-2 px-3 font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400">Outright</th>
                <th className="text-right py-2 px-3 font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400">Fwd Points</th>
                <th className="text-right py-2 px-3 font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400">Premium vs Spot</th>
              </tr>
            </thead>
            <tbody>
              {forwardCurve.map((pt) => (
                <tr key={pt.tenor} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <td className="py-2 px-3 font-mono text-xs font-bold">{pt.tenor}</td>
                  <td className="py-2 px-3 text-right font-mono text-xs">{pt.rate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="py-2 px-3 text-right font-mono text-xs text-zinc-500">+{pt.points}</td>
                  <td className="py-2 px-3 text-right font-mono text-xs text-zinc-500">{((pt.rate - spot) / spot * 100).toFixed(2)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RatesBoard;
